import { useState } from 'react';
import api from '../../utils/api';
import CameraFeed from './CameraFeed';
import ModalPortal from '../ModalPortal';
import {
  X, Pause, Play, Square, Thermometer, Layers, Gauge, Fan, Wifi,
  Droplets, CircleDot, Clock, RotateCcw, Pencil, AlertTriangle, Box,
} from 'lucide-react';

/**
 * 프린터 상세.
 *
 * 레이아웃 원칙: 넓은 화면에서는 "보는 것"(라이브뷰)과 "읽는 것"(수치)을 좌우로 나눈다.
 * 예전 단일 컬럼은 카메라가 세로를 독점하고 수치가 아래 좁은 띠에 눌려서,
 * 정작 확인하려던 온도·진행률을 읽기 어려웠다.
 */

const GCODE_LABEL = {
  RUNNING: '출력 중', PAUSE: '일시정지', FINISH: '출력 완료',
  IDLE: '대기', FAILED: '실패', PREPARE: '준비 중', SLICING: '슬라이싱',
};

function fmtMin(min) {
  if (min == null || min <= 0) return null;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h ? `${h}시간 ${m}분` : `${m}분`;
}

function etaText(min) {
  if (!min || min <= 0) return null;
  const d = new Date(Date.now() + min * 60000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} 예정`;
}

function fmtUsage(minutes) {
  if (minutes == null) return '-';
  if (minutes < 60) return `${minutes}분`;
  return `${Math.floor(minutes / 60)}시간`;
}

function fanPct(mqtt) {
  const v = parseInt(mqtt?.coolingFan);
  return isNaN(v) ? 0 : Math.round((v / 15) * 100);
}

/* ── 수치 타일 ── */
function Stat({ icon: Icon, label, value, sub, tone = 'default' }) {
  const toneCls = {
    default: 'text-gray-100',
    hot: 'text-orange-400',
    brand: 'text-brand',
  }[tone];
  return (
    <div className="rounded-xl bg-gray-800 border border-gray-700 px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} className="text-gray-500 shrink-0" />
        <span className="text-[10px] text-gray-500 truncate">{label}</span>
      </div>
      <div className={`font-bold tabular-nums leading-none ${toneCls}`}>
        {value ?? <span className="text-gray-600">-</span>}
      </div>
      {sub && <div className="text-[10px] text-gray-600 mt-1 tabular-nums">{sub}</div>}
    </div>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[11px] font-semibold text-gray-400">{children}</span>
      {right}
    </div>
  );
}

export default function PrinterDetailModal({
  printer, mqtt = {}, stocks = [], nozzles = [],
  onClose, onEdit, onFilament, onNozzle, onControl, errorMsg,
}) {
  const [reprintJobs, setReprintJobs] = useState([]);
  const [showReprint, setShowReprint] = useState(false);
  const [reprintTab, setReprintTab] = useState('this');
  const [busy, setBusy] = useState(false);

  const offline = printer.status === 'offline';
  const gstate = mqtt?.gcodeState;
  const printing = gstate === 'RUNNING';
  const paused = gstate === 'PAUSE';
  const finished = gstate === 'FINISH';
  const idle = !printing && !paused && !offline;
  const pct = Math.max(0, Math.min(100, Number(mqtt?.mcPercent) || 0));
  const remain = printing ? fmtMin(mqtt?.mcRemainingTime) : null;
  const eta = printing ? etaText(mqtt?.mcRemainingTime) : null;

  const filamentSlots = printer.filamentSlots || [];

  // 출력 오류와 HMS가 같은 문구를 담는 경우가 있다(예: "상단 덮개가 열려 있습니다").
  // 위에서 이미 보여준 내용을 정비 알림에서 한 번 더 늘어놓지 않는다.
  const fatalMsg = mqtt?.printErrorMsg || errorMsg || (gstate === 'FAILED' ? '출력에 실패했습니다.' : null);
  const norm = t => String(t || '').replace(/\s+/g, ' ').trim();
  const hmsList = (mqtt?.hmsErrors || []).filter(h => norm(h.msg) !== norm(fatalMsg));

  async function loadReprint(tab) {
    try {
      const url = tab === 'this'
        ? `/jobs/reprintable/list?printerId=${printer.id}`
        : '/jobs/reprintable/list';
      const { data } = await api.get(url);
      setReprintJobs(Array.isArray(data) ? data : []);
    } catch { setReprintJobs([]); }
    setReprintTab(tab);
    setShowReprint(true);
  }

  async function doReprint(jobId) {
    if (!confirm('이 출력물을 이 프린터에서 재출력할까요?')) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/reprint`, { printerId: printer.id });
      setShowReprint(false);
      alert('재출력이 시작되었습니다.');
    } catch (e) { alert(e.response?.data?.message || '재출력 실패'); }
    finally { setBusy(false); }
  }

  // 목록 배지와 같은 대비 기준을 쓴다 (밝은 배경에는 짙은 글자)
  const statusTone = offline ? 'bg-gray-600 text-[#ffffff]'
    : printing ? 'bg-brand text-[#06263a]'
    : paused ? 'bg-amber-400 text-[#3d2704]'
    : gstate === 'FAILED' ? 'bg-red-600 text-[#ffffff]'
    : 'bg-emerald-700 text-[#ffffff]';

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-50 bg-black/90 flex items-stretch sm:items-center justify-center sm:p-3" onClick={onClose}>
      <div
        className="bg-gray-900 w-full h-full sm:h-auto sm:max-h-[94vh] sm:rounded-2xl sm:max-w-[1360px] flex flex-col overflow-hidden border-0 sm:border sm:border-gray-700"
        onClick={e => e.stopPropagation()}>

        {/* ── 헤더 ── */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 shrink-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${printing ? 'bg-brand animate-pulse' : offline ? 'bg-gray-600' : 'bg-emerald-500'}`} />
          <h2 className="text-gray-100 font-bold text-base sm:text-lg truncate">{printer.name}</h2>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${statusTone}`}>
            {offline ? '오프라인' : (GCODE_LABEL[gstate] || '대기')}
          </span>
          {printer.model && (
            <span className="hidden sm:inline text-[11px] text-gray-500 shrink-0">
              {printer.model.replace('Bambu Lab ', '')}
            </span>
          )}
          <div className="flex-1" />
          {onEdit && (
            <button onClick={() => onEdit(printer)} title="프린터 설정"
              className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
              <Pencil size={15} />
            </button>
          )}
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* ── 본문: 넓으면 좌우 분할 ── */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden">

          {/* 좌: 라이브뷰 + 제어 */}
          <div className="lg:flex-1 lg:min-w-0 flex flex-col bg-gray-950 p-2 gap-2">
            <div className="relative rounded-xl overflow-hidden bg-gray-800 aspect-[16/10]">
              {offline ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <Box size={30} className="text-gray-700" />
                  <span className="text-xs text-gray-600">오프라인 — 영상을 받을 수 없습니다</span>
                </div>
              ) : (
                <CameraFeed
                  printerId={printer.id}
                  cameraStyle={printer.cameraStyle}
                  className="w-full h-full !object-cover"
                  pollInterval={500}
                />
              )}

              {/* 출력 중이면 영상 위에 진행 상황을 얹는다 — 눈이 가는 곳에 붙인다 */}
              {printing && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent px-3 pt-10 pb-2.5">
                  {mqtt?.jobName && (
                    <div className="text-[11px] text-[#cbd5e1] truncate mb-1" title={mqtt.jobName}>
                      {mqtt.jobName}
                    </div>
                  )}
                  <div className="flex items-end justify-between mb-1.5">
                    <span className="text-3xl font-black text-[#ffffff] tabular-nums leading-none">{pct}<span className="text-base">%</span></span>
                    <div className="text-right leading-tight">
                      {remain && <div className="text-sm font-bold text-[#ffffff] tabular-nums">{remain} 남음</div>}
                      {eta && <div className="text-[11px] text-[#cbd5e1] tabular-nums">{eta}</div>}
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full bg-brand rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}
            </div>

            {/* 제어 — 중요한 액션이라 영상 바로 아래에 크게 둔다 */}
            {(printing || paused) && onControl && (
              <div className="flex gap-2 shrink-0">
                {printing && (
                  <button onClick={() => onControl('pause')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm font-semibold hover:bg-amber-500/25 transition-colors">
                    <Pause size={15} /> 일시정지
                  </button>
                )}
                {paused && (
                  <button onClick={() => onControl('resume')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/25 transition-colors">
                    <Play size={15} /> 재개
                  </button>
                )}
                <button onClick={() => { if (confirm('출력을 중지할까요? 되돌릴 수 없습니다.')) onControl('stop'); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-sm font-semibold hover:bg-red-500/25 transition-colors">
                  <Square size={15} /> 중지
                </button>
              </div>
            )}
          </div>

          {/* 우: 수치 · 소모품 */}
          <div className="lg:w-[320px] lg:shrink-0 lg:border-l border-gray-800 p-3 flex flex-col gap-3 lg:overflow-y-auto">

            {/* SD 카드가 없으면 파일 전송이 불가능하다. 상세에서도 바로 보이게 한다. */}
            {mqtt?.sdcard === false && (
              <div className="flex items-start gap-2 rounded-xl bg-amber-400/15 border border-amber-400/40 px-3 py-2.5">
                <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-amber-600 text-xs leading-relaxed">
                  microSD 카드가 없습니다. 카드를 넣어야 이 프린터로 출력 파일을 보낼 수 있습니다.
                </p>
              </div>
            )}

            {/* 출력 실패 — 즉시 조치가 필요한 오류 */}
            {fatalMsg && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <AlertTriangle size={13} className="text-red-500 shrink-0" />
                  <span className="text-[11px] font-semibold text-red-500">출력 오류</span>
                  {mqtt?.printError > 0 && (
                    <span className="text-[10px] text-gray-500 ml-auto tabular-nums">코드 {mqtt.printError}</span>
                  )}
                </div>
                <p className="text-red-600 text-xs leading-relaxed">{fatalMsg}</p>
              </div>
            )}

            {/* HMS — 윤활·청소 같은 정비 알림. 급하지 않지만 쌓이면 고장으로 이어진다. */}
            {hmsList.length > 0 && (
              <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                  <span className="text-[11px] font-semibold text-amber-600">정비 알림 {hmsList.length}건</span>
                </div>
                <ul className="flex flex-col gap-1">
                  {hmsList.map((h, i) => (
                    <li key={h.code || i} className="text-[11px] text-amber-700 leading-relaxed flex gap-1.5">
                      <span className="text-amber-500 shrink-0">·</span>
                      <span>{h.msg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 온도 */}
            <div>
              <SectionTitle>온도</SectionTitle>
              <div className="grid grid-cols-3 gap-2">
                <Stat icon={Thermometer} label="노즐"
                  value={mqtt?.nozzleTemper != null ? `${Math.round(mqtt.nozzleTemper)}°` : null}
                  sub={mqtt?.nozzleTarget > 0 ? `목표 ${Math.round(mqtt.nozzleTarget)}°` : null}
                  tone={mqtt?.nozzleTemper > 50 ? 'hot' : 'default'} />
                <Stat icon={Thermometer} label="베드"
                  value={mqtt?.bedTemper != null ? `${Math.round(mqtt.bedTemper)}°` : null}
                  sub={mqtt?.bedTarget > 0 ? `목표 ${Math.round(mqtt.bedTarget)}°` : null}
                  tone={mqtt?.bedTemper > 50 ? 'hot' : 'default'} />
                <Stat icon={Thermometer} label="챔버"
                  value={mqtt?.chamberTemper != null ? `${Math.round(mqtt.chamberTemper)}°` : null} />
              </div>
            </div>

            {/* 진행 상세 */}
            <div>
              <SectionTitle>진행</SectionTitle>
              <div className="grid grid-cols-3 gap-2">
                <Stat icon={Layers} label="레이어"
                  value={mqtt?.layerNum != null ? mqtt.layerNum : null}
                  sub={mqtt?.totalLayerNum ? `전체 ${mqtt.totalLayerNum}` : null} />
                <Stat icon={Gauge} label="속도" value={mqtt?.spdMag != null ? `${mqtt.spdMag}%` : null} />
                <Stat icon={Fan} label="팬" value={mqtt ? `${fanPct(mqtt)}%` : null} />
              </div>
              {mqtt?.wifiSignal && (
                <div className="flex items-center gap-1.5 mt-2 text-[10px] text-gray-600">
                  <Wifi size={10} /> 신호 {mqtt.wifiSignal}
                </div>
              )}
            </div>

            {/* 필라멘트 */}
            <div>
              <SectionTitle>필라멘트</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                {[0, 1].map(slotNo => {
                  const slot = filamentSlots.find(s => s.slotNo === slotNo);
                  const low = slot && slot.remainingWeightGram <= 150;
                  const p = slot?.totalWeightGram
                    ? Math.round((slot.remainingWeightGram / slot.totalWeightGram) * 100) : 0;
                  return (
                    <button key={slotNo}
                      onClick={() => onFilament?.(printer, slotNo === 0 ? 'left' : 'right', slot)}
                      className="rounded-xl bg-gray-800 border border-gray-700 p-2.5 text-left hover:border-brand/50 transition-colors">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Droplets size={10} className="text-gray-500" />
                        <span className="text-[10px] font-bold text-gray-400">{slotNo === 0 ? 'L' : 'R'}</span>
                        {slot?.colorHex && (
                          <span className="w-3 h-3 rounded-full border border-gray-600 ml-auto"
                            style={{ backgroundColor: slot.colorHex }} />
                        )}
                      </div>
                      {slot ? (
                        <>
                          <p className="text-xs text-gray-100 font-medium truncate">{slot.colorName || slot.material}</p>
                          <div className="h-1 rounded-full bg-gray-700 my-1.5 overflow-hidden">
                            <div className={`h-full rounded-full ${low ? 'bg-amber-400' : 'bg-brand'}`}
                              style={{ width: `${Math.max(3, p)}%` }} />
                          </div>
                          <span className={`text-[10px] tabular-nums ${low ? 'text-amber-400 font-semibold' : 'text-gray-500'}`}>
                            {slot.remainingWeightGram}g {low && '· 부족'}
                          </span>
                        </>
                      ) : (
                        <p className="text-[11px] text-gray-600">비어있음 · 눌러서 장착</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 노즐 */}
            <div>
              <SectionTitle>노즐</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                {[0, 1].map(slotNo => {
                  const nz = printer.nozzles?.find(n => n.slotNo === slotNo);
                  return (
                    <button key={slotNo}
                      onClick={() => onNozzle?.(printer, slotNo === 0 ? 'left' : 'right')}
                      className="rounded-xl bg-gray-800 border border-gray-700 p-2.5 text-left hover:border-brand/50 transition-colors">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <CircleDot size={10} className="text-gray-500" />
                        <span className="text-[10px] font-bold text-gray-400">{slotNo === 0 ? 'L' : 'R'}</span>
                      </div>
                      {nz ? (
                        <>
                          <p className="text-xs text-gray-100 font-medium truncate">{nz.material} {nz.diameter}mm</p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-1 mt-1">
                            <Clock size={9} /> {fmtUsage(nz.usageMinutes)} 사용
                          </p>
                        </>
                      ) : (
                        <p className="text-[11px] text-gray-600">비어있음 · 눌러서 장착</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 출력 이력 / 재출력 */}
            {idle && (
              <div className="mt-auto">
                {!showReprint ? (
                  <button onClick={() => loadReprint('this')}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-800 border border-gray-700 text-sm text-gray-300 hover:text-white hover:border-brand/40 transition-colors">
                    <RotateCcw size={14} /> 출력 이력 · 재출력
                  </button>
                ) : (
                  <div className="rounded-xl bg-gray-800 border border-gray-700 p-2.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-semibold text-gray-300">출력 이력</span>
                      <button onClick={() => setShowReprint(false)} className="text-gray-500 hover:text-white">
                        <X size={13} />
                      </button>
                    </div>
                    <div className="flex gap-1 mb-2">
                      {[['this', '이 프린터'], ['all', '전체']].map(([k, l]) => (
                        <button key={k} onClick={() => loadReprint(k)}
                          className={`flex-1 py-1.5 text-[11px] rounded-lg transition-colors
                            ${reprintTab === k ? 'bg-brand text-white font-semibold' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                    <div className="max-h-56 overflow-y-auto flex flex-col gap-1">
                      {reprintJobs.length === 0 ? (
                        <p className="text-[11px] text-gray-600 text-center py-5">이력이 없습니다.</p>
                      ) : reprintJobs.map(j => (
                        <button key={j.id} disabled={busy} onClick={() => doReprint(j.id)}
                          className="text-left px-2.5 py-2 rounded-lg bg-gray-900 hover:bg-gray-700 disabled:opacity-50 transition-colors">
                          <p className="text-[11px] text-gray-100 truncate">{j.productName || j.threeMfPath || `작업 #${j.id}`}</p>
                          <p className="text-[10px] text-gray-600">
                            {j.printerName || '-'}{j.completedAt ? ` · ${new Date(j.completedAt).toLocaleDateString('ko-KR')}` : ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
